import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Classes } from '@blueprintjs/core'

import apis from 'browser/app/models/apis'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { JSONSchemaResolver } from 'shared-libs/resolvers/json-schema-resolver'
import {
  addFilter,
  buildDisplayFilters,
  getExclusiveSetKey,
  getFilterFeedbackText,
  getFilterIdentity,
  getNaturalSpeechFilterText,
  getNormalizedFiltersFromProps,
  IDisplayFilter,
  ISearchFilterConfig,
  normalizeAvailableFilter,
  removeFilterByIdentity,
} from 'shared-libs/helpers/search-filters'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_entity-search-filters.scss'

export type { ISearchFilterConfig, IDisplayFilter }

interface IEntitySearchFiltersProps {
  filters?: ISearchFilterConfig[]
  availableFilters?: ISearchFilterConfig[]
  entitySchema?: any
  onFiltersChange?: (filters: ISearchFilterConfig[], availableFilters?: ISearchFilterConfig[]) => void
  className?: string
}

interface IEntitySearchFiltersState {
  isSheetOpen: boolean
  draftAppliedFilters: ISearchFilterConfig[]
  draftAvailableFilters: ISearchFilterConfig[]
  exclusiveSetFeedbackText?: string
  highlightedAppliedFilterIdentity?: string
  pillContainerWidth: number
  pillRowsByKey: Record<string, number>
}

const EXCLUSIVE_SET_FEEDBACK_DURATION_MS = 2500

/**
 * Filter pill bar + management sheet for entity search.
 *
 * Header renders applied filters as compact `label: value` pills capped at
 * 2 rows with a "+ N more" overflow pill. Tapping opens a modal with Applied
 * and Available lists; Apply commits changes via `onFiltersChange`.
 */
export class EntitySearchFilters extends React.Component<
  IEntitySearchFiltersProps,
  IEntitySearchFiltersState
> {
  private schemaResolver: JSONSchemaResolver
  private exclusiveSetFeedbackTimeout?: ReturnType<typeof setTimeout>
  private pillContainerRef = React.createRef<HTMLDivElement>()
  private pillContainerObserver?: ResizeObserver
  private highlightClearTimeout?: ReturnType<typeof setTimeout>
  private openSheetNode?: Element
  private pillElementsByKey: Record<string, HTMLDivElement | null> = {}

  constructor(props: IEntitySearchFiltersProps) {
    super(props)
    this.schemaResolver = new JSONSchemaResolver(apis)
    this.state = {
      isSheetOpen: false,
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
      highlightedAppliedFilterIdentity: undefined,
      pillContainerWidth: 0,
      pillRowsByKey: {},
    }
  }

  public componentDidMount() {
    if (this.pillContainerRef.current && typeof ResizeObserver !== 'undefined') {
      this.pillContainerObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        const nextWidth = Math.round(entry.contentRect.width)
        this.setState((prev) =>
          prev.pillContainerWidth === nextWidth
            ? null
            : { pillContainerWidth: nextWidth, pillRowsByKey: {} }
        )
      })
      this.pillContainerObserver.observe(this.pillContainerRef.current)
    }
  }

  public componentWillUnmount() {
    this.pillContainerObserver?.disconnect()
    this.clearExclusiveSetFeedbackTimeout()
    if (this.highlightClearTimeout) clearTimeout(this.highlightClearTimeout)
  }

  public render() {
    const { className } = this.props
    const { appliedFilters: normalizedApplied, availableFilters: normalizedAvailable } =
      getNormalizedFiltersFromProps(this.props.filters, this.props.availableFilters)

    const displayFilters = this.getDisplayFilters(normalizedApplied)
    const availableDisplayFilters = this.getDisplayFilters(normalizedAvailable)

    const hasVisibleApplied = !_.isEmpty(displayFilters)
    const hasVisibleAvailable = !_.isEmpty(availableDisplayFilters)
    const isSheetOpenable = hasVisibleAvailable || hasVisibleApplied

    if (!hasVisibleApplied && !hasVisibleAvailable) {
      return null
    }

    const { visibleDisplayFilters, hiddenFilterCount } = this.getVisibleAppliedPills(displayFilters)

    let pillsToRender = visibleDisplayFilters
    let morePillsCount = hiddenFilterCount

    if (morePillsCount > 0 && pillsToRender.length > 0) {
      pillsToRender = pillsToRender.slice(0, -1)
      morePillsCount += 1
    }

    return (
      <div
        className={classNames('ui2-entitySearchFilters', className, {
          'ui2-entitySearchFilters--openable': isSheetOpenable,
        })}
      >
        <div className='ui2-entitySearchFilters-pills' ref={this.pillContainerRef}>
          {_.map(pillsToRender, (item) => {
            const { key, label, value, isClearable, identity } = item
            return (
              <div
                key={key}
                className={classNames(
                  'ui2-entitySearchFilters-pill',
                  'ui2-entitySearchFilters-pill--clickable',
                  { 'ui2-entitySearchFilters-pill--fixed': !isClearable }
                )}
                ref={(el) => this.registerPillRef(key, el)}
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  this.handleOpenSheetForFilter(identity)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    this.handleOpenSheetForFilter(identity)
                  }
                }}
              >
                <span className='ui2-entitySearchFilters-pillLabel'>{label}:</span>
                <span className='ui2-entitySearchFilters-pillValue'>{value}</span>
                {isClearable && (
                  <button
                    type='button'
                    className='ui2-entitySearchFilters-pillClear'
                    aria-label={`Remove ${label} filter`}
                    onClick={(e) => {
                      e.stopPropagation()
                      this.handleQuickRemoveFilter(item)
                    }}
                  >
                    <UI2Icon name={UI2IconNames.X} size={10} />
                  </button>
                )}
              </div>
            )
          })}
          {morePillsCount > 0 && (
            <div
              className='ui2-entitySearchFilters-pill ui2-entitySearchFilters-pill--more ui2-entitySearchFilters-pill--clickable'
              role='button'
              tabIndex={0}
              onClick={this.handleOpenSheet}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  this.handleOpenSheet()
                }
              }}
            >
              {`+ ${morePillsCount} more`}
            </div>
          )}
          {hasVisibleAvailable && (
            <button
              type='button'
              className='ui2-entitySearchFilters-pill ui2-entitySearchFilters-pill--addAction'
              onClick={this.handleOpenSheet}
            >
              + Add Filters
            </button>
          )}
        </div>
      </div>
    )
  }

  private registerPillRef = (key: string, el: HTMLDivElement | null) => {
    this.pillElementsByKey[key] = el
    if (!el) return
    requestAnimationFrame(() => {
      const containerTop = this.pillContainerRef.current?.getBoundingClientRect().top ?? 0
      const rect = el.getBoundingClientRect()
      const row = Math.round(rect.top - containerTop)
      this.setState((prev) => {
        if (prev.pillRowsByKey[key] === row) return null
        return { pillRowsByKey: { ...prev.pillRowsByKey, [key]: row } }
      })
    })
  }

  private getVisibleAppliedPills = (displayFilters: IDisplayFilter[]) => {
    const { pillRowsByKey } = this.state
    const hasMeasuredRows = _.every(displayFilters, (f) => !_.isNil(pillRowsByKey[f.key]))
    if (!hasMeasuredRows) {
      return { visibleDisplayFilters: displayFilters, hiddenFilterCount: 0 }
    }
    const uniqueRows = _.sortBy(_.uniq(_.map(displayFilters, (f) => pillRowsByKey[f.key])))
    if (uniqueRows.length <= 2) {
      return { visibleDisplayFilters: displayFilters, hiddenFilterCount: 0 }
    }
    const secondRow = uniqueRows[1]
    const visibleDisplayFilters = _.filter(displayFilters, (f) => pillRowsByKey[f.key] <= secondRow)
    return {
      visibleDisplayFilters,
      hiddenFilterCount: Math.max(0, displayFilters.length - visibleDisplayFilters.length),
    }
  }

  private handleQuickRemoveFilter = (item: IDisplayFilter) => {
    if (!item.isClearable) return
    const nextFilters = removeFilterByIdentity(this.props.filters ?? [], item.identity)
    const nextAvailable = addFilter(
      this.props.availableFilters ?? [],
      normalizeAvailableFilter(item.filter)
    )
    this.props.onFiltersChange?.(nextFilters, nextAvailable)
  }

  private handleOpenSheetForFilter = (identity: string) => {
    this.handleOpenSheet()
    this.highlightAppliedFilterRow(identity)
  }

  private handleOpenSheet = () => {
    const { appliedFilters: normalizedApplied, availableFilters: normalizedAvailable } =
      getNormalizedFiltersFromProps(this.props.filters, this.props.availableFilters)

    this.setState({
      isSheetOpen: true,
      draftAppliedFilters: _.cloneDeep(normalizedApplied),
      draftAvailableFilters: _.cloneDeep(normalizedAvailable),
    })

    this.openSheetNode = OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={false}
        modalDialogClassName='ui2-entitySearchFilters-sheetDialog'
        modalPaperClassName='ui2-entitySearchFilters-sheetPaper'
      >
        <EntitySearchFiltersSheet
          ownerRef={this}
          onCancel={this.handleCancelSheet}
          onApply={this.handleApplySheet}
        />
      </Modal>
    ) as Element
  }

  private closeSheetOverlay = () => {
    if (this.openSheetNode) {
      OverlayManager.closeOverlay(this.openSheetNode as HTMLElement)
      this.openSheetNode = undefined
    }
  }

  private handleCancelSheet = () => {
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({
      isSheetOpen: false,
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
    })
    this.closeSheetOverlay()
  }

  private handleApplySheet = () => {
    this.props.onFiltersChange?.(this.state.draftAppliedFilters, this.state.draftAvailableFilters)
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({
      isSheetOpen: false,
      draftAppliedFilters: [],
      draftAvailableFilters: [],
      exclusiveSetFeedbackText: undefined,
    })
    this.closeSheetOverlay()
  }

  public handleMoveFilterToAvailable = (filterItem: IDisplayFilter) => {
    if (!filterItem.isClearable) return
    this.setState((prev) => ({
      draftAppliedFilters: removeFilterByIdentity(prev.draftAppliedFilters, filterItem.identity),
      draftAvailableFilters: addFilter(
        prev.draftAvailableFilters,
        normalizeAvailableFilter(filterItem.filter)
      ),
    }))
  }

  public handleMoveFilterToApplied = (filterItem: IDisplayFilter) => {
    let replacementFeedback: { appliedText: string; replacedText: string } | undefined
    let addedIdentity: string | undefined

    this.setState(
      (prev) => {
        const filterToApply = normalizeAvailableFilter(filterItem.filter)
        const filterToApplyIdentity = getFilterIdentity(filterToApply)
        const filterExclusiveSetKey = getExclusiveSetKey(filterToApply)
        addedIdentity = filterToApplyIdentity

        let nextAvailable = removeFilterByIdentity(prev.draftAvailableFilters, filterItem.identity)
        let nextApplied = prev.draftAppliedFilters

        if (!_.isNil(filterExclusiveSetKey)) {
          const conflicting = _.filter(nextApplied, (a) => {
            return (
              getExclusiveSetKey(a) === filterExclusiveSetKey &&
              getFilterIdentity(a) !== filterToApplyIdentity
            )
          })
          const firstConflict = _.first(conflicting)
          if (firstConflict) {
            replacementFeedback = {
              appliedText: getFilterFeedbackText(
                filterToApply,
                this.props.entitySchema,
                this.schemaResolver
              ),
              replacedText: getFilterFeedbackText(
                firstConflict,
                this.props.entitySchema,
                this.schemaResolver
              ),
            }
          }
          nextApplied = _.filter(nextApplied, (a) => {
            return (
              getExclusiveSetKey(a) !== filterExclusiveSetKey ||
              getFilterIdentity(a) === filterToApplyIdentity
            )
          })
          nextAvailable = _.reduce(
            conflicting,
            (acc, cf) => addFilter(acc, normalizeAvailableFilter(cf)),
            nextAvailable
          )
        }

        nextApplied = addFilter(nextApplied, filterToApply)
        return { draftAppliedFilters: nextApplied, draftAvailableFilters: nextAvailable }
      },
      () => {
        if (addedIdentity) this.highlightAppliedFilterRow(addedIdentity)
        if (replacementFeedback) {
          this.showExclusiveSetFeedback(
            `Replaced "${replacementFeedback.replacedText}" with "${replacementFeedback.appliedText}"`
          )
        }
      }
    )
  }

  public getDisplayFilters = (filters: ISearchFilterConfig[]): IDisplayFilter[] =>
    buildDisplayFilters(filters, this.props.entitySchema, this.schemaResolver)

  public getFilterDescriptionText = (item: IDisplayFilter): string =>
    item.helpText ||
    getNaturalSpeechFilterText(
      item.filter,
      item.label,
      item.value,
      this.props.entitySchema?.translations
    )

  private highlightAppliedFilterRow = (identity: string) => {
    this.setState({ highlightedAppliedFilterIdentity: identity })
    if (this.highlightClearTimeout) clearTimeout(this.highlightClearTimeout)
    this.highlightClearTimeout = setTimeout(() => {
      this.setState({ highlightedAppliedFilterIdentity: undefined })
      this.highlightClearTimeout = undefined
    }, 1800)
  }

  private showExclusiveSetFeedback = (message: string) => {
    this.clearExclusiveSetFeedbackTimeout()
    this.setState({ exclusiveSetFeedbackText: message })
    this.exclusiveSetFeedbackTimeout = setTimeout(() => {
      this.setState({ exclusiveSetFeedbackText: undefined })
      this.exclusiveSetFeedbackTimeout = undefined
    }, EXCLUSIVE_SET_FEEDBACK_DURATION_MS)
  }

  private clearExclusiveSetFeedbackTimeout = () => {
    if (this.exclusiveSetFeedbackTimeout) {
      clearTimeout(this.exclusiveSetFeedbackTimeout)
      this.exclusiveSetFeedbackTimeout = undefined
    }
  }

  public get draftAppliedFilters(): ISearchFilterConfig[] {
    return this.state.draftAppliedFilters
  }
  public get draftAvailableFilters(): ISearchFilterConfig[] {
    return this.state.draftAvailableFilters
  }
  public get exclusiveSetFeedbackText(): string | undefined {
    return this.state.exclusiveSetFeedbackText
  }
  public get highlightedAppliedFilterIdentity(): string | undefined {
    return this.state.highlightedAppliedFilterIdentity
  }
}

// ─── Sheet body ──────────────────────────────────────────────────────────────

interface IEntitySearchFiltersSheetProps {
  ownerRef: EntitySearchFilters
  onCancel: () => void
  onApply: () => void
}

class EntitySearchFiltersSheet extends React.Component<IEntitySearchFiltersSheetProps> {
  private unsubscribe?: () => void

  public componentDidMount() {
    const originalSetState = this.props.ownerRef.setState.bind(this.props.ownerRef)
    this.props.ownerRef.setState = ((...args: any[]) => {
      const cb = args[1]
      const wrappedCb = () => {
        this.forceUpdate()
        cb?.()
      }
      return originalSetState(args[0], wrappedCb)
    }) as any
    this.unsubscribe = () => {
      this.props.ownerRef.setState = originalSetState
    }
  }

  public componentWillUnmount() {
    this.unsubscribe?.()
  }

  public render() {
    const owner = this.props.ownerRef
    const draftAppliedDisplayFilters = owner.getDisplayFilters(owner.draftAppliedFilters)
    const draftAvailableDisplayFilters = owner.getDisplayFilters(owner.draftAvailableFilters)
    const exclusiveSetFeedbackText = owner.exclusiveSetFeedbackText

    return (
      <div className='ui2-entitySearchFilters-sheet'>
        <div className='ui2-entitySearchFilters-sheet-header'>
          <h5 className='ui2-entitySearchFilters-sheet-title'>Filters</h5>
        </div>

        <div className='ui2-entitySearchFilters-sheet-body'>
          <div className='ui2-entitySearchFilters-sheet-section'>
            <div className='ui2-entitySearchFilters-sheet-sectionTitle'>
              Applied Filters ({draftAppliedDisplayFilters.length})
            </div>
            <div className='ui2-entitySearchFilters-sheet-list'>
              {_.isEmpty(draftAppliedDisplayFilters) ? (
                <div className='ui2-entitySearchFilters-sheet-emptyState'>No applied filters</div>
              ) : (
                _.map(draftAppliedDisplayFilters, (item) => this.renderAppliedRow(item))
              )}
            </div>
          </div>

          {!_.isNil(exclusiveSetFeedbackText) && (
            <div className='ui2-entitySearchFilters-sheet-feedback'>{exclusiveSetFeedbackText}</div>
          )}

          <div className='ui2-entitySearchFilters-sheet-section'>
            <div className='ui2-entitySearchFilters-sheet-sectionTitle'>Available Filters</div>
            <div className='ui2-entitySearchFilters-sheet-list'>
              {_.isEmpty(draftAvailableDisplayFilters) ? (
                <div className='ui2-entitySearchFilters-sheet-emptyState'>
                  No available filters
                </div>
              ) : (
                _.map(draftAvailableDisplayFilters, (item) => this.renderAvailableRow(item))
              )}
            </div>
          </div>
        </div>

        <div className='ui2-entitySearchFilters-sheet-footer'>
          <button
            type='button'
            className={classNames(Classes.BUTTON, 'ui2-entitySearchFilters-sheet-cancelButton')}
            onClick={this.props.onCancel}
          >
            Cancel
          </button>
          <button
            type='button'
            className={classNames(
              Classes.BUTTON,
              Classes.INTENT_PRIMARY,
              'ui2-entitySearchFilters-sheet-applyButton'
            )}
            onClick={this.props.onApply}
          >
            Apply
          </button>
        </div>
      </div>
    )
  }

  private renderAppliedRow = (item: IDisplayFilter) => {
    const owner = this.props.ownerRef
    const descriptionText = owner.getFilterDescriptionText(item)
    const isHighlighted = owner.highlightedAppliedFilterIdentity === item.identity

    return (
      <button
        key={item.key}
        type='button'
        className={classNames('ui2-entitySearchFilters-sheet-row', {
          'ui2-entitySearchFilters-sheet-row--highlighted': isHighlighted,
          'ui2-entitySearchFilters-sheet-row--nonClearable': !item.isClearable,
        })}
        disabled={!item.isClearable}
        onClick={() => owner.handleMoveFilterToAvailable(item)}
      >
        <div className='ui2-entitySearchFilters-sheet-rowMain'>
          <div
            className={classNames('ui2-entitySearchFilters-pill', 'ui2-entitySearchFilters-pill--sheet', {
              'ui2-entitySearchFilters-pill--fixed': !item.isClearable,
            })}
          >
            <span className='ui2-entitySearchFilters-pillLabel'>{item.label}:</span>
            <span className='ui2-entitySearchFilters-pillValue'>{item.value}</span>
          </div>
          <div className='ui2-entitySearchFilters-sheet-rowHelp'>{descriptionText}</div>
        </div>
        {item.isClearable ? (
          <div className='ui2-entitySearchFilters-sheet-rowIcon'>
            <UI2Icon name={UI2IconNames.X} />
          </div>
        ) : (
          <div className='ui2-entitySearchFilters-sheet-rowNonClearableHint'>Cannot remove</div>
        )}
      </button>
    )
  }

  private renderAvailableRow = (item: IDisplayFilter) => {
    const owner = this.props.ownerRef
    const descriptionText = owner.getFilterDescriptionText(item)

    return (
      <button
        key={item.key}
        type='button'
        className='ui2-entitySearchFilters-sheet-row'
        onClick={() => owner.handleMoveFilterToApplied(item)}
      >
        <div className='ui2-entitySearchFilters-sheet-rowMain'>
          <div className={classNames('ui2-entitySearchFilters-pill', 'ui2-entitySearchFilters-pill--sheet')}>
            <span className='ui2-entitySearchFilters-pillLabel'>{item.label}:</span>
            <span className='ui2-entitySearchFilters-pillValue'>{item.value}</span>
          </div>
          <div className='ui2-entitySearchFilters-sheet-rowHelp'>{descriptionText}</div>
        </div>
        <div className='ui2-entitySearchFilters-sheet-rowIcon ui2-entitySearchFilters-sheet-rowIcon--add'>
          <UI2Icon name={UI2IconNames.PLUS} />
        </div>
      </button>
    )
  }
}
